<script setup lang="ts">
  import AppSidebar from '@leaflink/stash/AppSidebar.vue';
  import ContextSwitcher from '@leaflink/stash/ContextSwitcher.vue';
  import isEmpty from 'lodash-es/isEmpty';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';

  import { APPS, ROUTE_NAMES, SPLIT_NAMES } from '@/constants';
  import { useAppConfigStore } from '@/piniaStores/appConfig/appConfig.store';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useShopStore } from '@/piniaStores/shop/shop.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import { track } from '@/track';
  import canStateAccessContextSwitcher from '@/utils/canStateAccessContextSwitcher';

  import AppNavigation from './AppNavigation/AppNavigation.vue';
  import navConfig, {
    type AppNavigationConfigItem,
    type MenuState,
    type ResolvedAppNavigationConfigItem,
  } from './AppNavigation/config';
  import CompanyPicker from './CompanyPicker/CompanyPicker.vue';
  import EnvironmentDrawer from './EnvironmentDrawer.vue';
  import EnvironmentDrawerLegacy from './EnvironmentDrawerLegacy.vue';
  import PastDueAlert from './PastDueAlert.vue';

  interface AppSidebarProps {
    isOpen: boolean;
  }

  const props = defineProps<AppSidebarProps>();

  const emit = defineEmits<{
    (e: 'navigate'): void;
    (e: 'dismiss'): void;
  }>();

  const appConfigStore = useAppConfigStore();

  const route = useRoute();
  const envName = computed(() => appConfigStore.buildInfo?.envName.toLowerCase() || 'dev');

  const companyStore = useCompanyStore();
  const shopStore = useShopStore();
  const userStore = useUserStore();

  const { activeCompany, badgeCounts, isAppLoaded, isDelinquent } = storeToRefs(companyStore);

  const { activeCampaign } = storeToRefs(shopStore);

  const { getIsSplitEnabled, user, userActiveCompany } = storeToRefs(userStore);

  function reduceNavItems(
    acc: ResolvedAppNavigationConfigItem[],
    item: AppNavigationConfigItem | ((menuState: MenuState) => ResolvedAppNavigationConfigItem),
  ) {
    const menuState = {
      activeCampaign: activeCampaign.value,
      activeCompany: activeCompany.value,
      currentRoute: route,
      isSplitEnabled: getIsSplitEnabled.value,
      user: user.value,
      userCompany: userActiveCompany.value,
      badgeCounts: badgeCounts.value,
    };
    let obj: ResolvedAppNavigationConfigItem = {
      id: '',
      label: '',
      icon: 'mj-leaf',
      visible: false,
    };

    if (isEmpty(userActiveCompany.value) || isEmpty(activeCompany.value)) {
      return acc;
    }

    if (typeof item === 'function') {
      obj = item(menuState);
    } else {
      Object.keys(item).forEach((key) => {
        if (key === 'children' && typeof item[key] !== 'function') {
          obj[key] = item[key]?.reduce(reduceNavItems, []);
        } else {
          obj[key] = typeof item[key] === 'function' ? item[key](menuState) : item[key];
        }
      });
    }

    if ('visible' in obj && obj.visible) {
      acc.push(obj);
    }

    return acc;
  }

  const navItems = computed(() => {
    if (isEmpty(user.value)) {
      return [];
    }

    const items = navConfig.reduce(reduceNavItems, []);

    return items;
  });

  /**
   * Hides navigation if user is on the join payments page
   */
  const hideNav = computed(() => {
    return [ROUTE_NAMES.JOIN_TODAY_PAYMENTS, ROUTE_NAMES.PAYMENT_PROGRAM_SELECT].includes(route.name as string);
  });

  /**
   * The current active app name for the context switcher
   */
  const activeApp = computed(() => {
    return hideNav.value ? APPS.PAYMENTS : APPS.MARKETPLACE;
  });

  const portalAccess = computed(() => {
    return user.value.platformAccess.access;
  });

  const routeQuery = computed(() => {
    if (!getIsSplitEnabled.value(SPLIT_NAMES.DIRECT_PAY_ENABLED_STATES) || !activeCompany.value) {
      return undefined;
    }

    if (activeCompany.value.buyer) {
      return {
        type: 'buyer-direct',
      };
    }

    return {
      type: 'seller-direct',
    };
  });

  const isContextSwitcherEnabled = computed(() => {
    if (!activeCompany.value) {
      return false;
    }

    const isValidState = canStateAccessContextSwitcher({ state: activeCompany.value.state });
    const hasPaymentsAccess = !!portalAccess.value?.paymentsPortal;

    return (isValidState || hasPaymentsAccess) && !activeCompany.value.isLogisticsOnly;
  });

  const isEnvDrawerVisible = computed(() => {
    return appConfigStore.buildInfo && envName.value !== 'prod' && envName.value !== 'sandbox';
  });

  const showPastDueAlert = computed(() => {
    return (
      activeCompany.value?.seller &&
      activeCompany.value.billing.unpaidOverdueCount > 0 &&
      !isDelinquent.value(getIsSplitEnabled.value(SPLIT_NAMES.DELINQUENT_LOCKDOWN))
    );
  });

  function onContentSwitch(locations) {
    track('Context Switcher Clicked', locations);
    emit('navigate');
  }
</script>

<template>
  <AppSidebar class="mp-app-sidebar" :is-open="props.isOpen" @dismiss="emit('dismiss')">
    <template #app-context>
      <ContextSwitcher
        v-if="isAppLoaded && isContextSwitcherEnabled"
        data-test="sidebar|contextSwitcher"
        :active-app="activeApp"
        :limited-access-url="ROUTE_NAMES.JOIN_TODAY_PAYMENTS"
        :route-query="routeQuery"
        :portal-access="portalAccess"
        @click="onContentSwitch"
      />
    </template>
    <template #company-context>
      <CompanyPicker
        v-if="isAppLoaded && !isEmpty(activeCompany)"
        data-test="sidebar|companyPicker"
        @navigate="emit('navigate')"
      />
      <PastDueAlert
        v-if="!hideNav && showPastDueAlert"
        data-test="sidebar|pastDueAlert"
        class="!tw-mt-6"
        @navigate="emit('navigate')"
      />
    </template>
    <template #navigation>
      <AppNavigation
        v-if="!hideNav && navItems"
        data-test="sidebar|appNavigation"
        :items="navItems"
        @navigate="emit('navigate')"
      />
    </template>
    <template #footer>
      <EnvironmentDrawer v-if="isEnvDrawerVisible && getIsSplitEnabled(SPLIT_NAMES.SSR_IFRAME)" />
      <EnvironmentDrawerLegacy v-else-if="isEnvDrawerVisible" :build-info="appConfigStore.buildInfo" />
    </template>
  </AppSidebar>
</template>

<style scoped>
  .mp-app-sidebar.stash-app-sidebar--is-open {
    z-index: 300 !important; /* the "page" level. This needs to be lower than the z-index of the AppLogoBurger. */
  }

  .mp-app-sidebar :deep(.stash-app-sidebar__aside) {
    inset: 0;
    height: 100dvh;
  }

  .mp-app-sidebar :deep(.stash-app-sidebar__aside > nav) {
    overflow-y: auto;
  }
</style>
